import styled from '@emotion/styled';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ModalSidebar from '../../../../components/ModalSidebar';
import { useUserInfo } from '../../../../context/UserInfo';
import { useIsManagerMode } from '../../../../hooks/useIsManagerMode';
import { GoalFilter } from '../../Goals2/GoalsFilter/GoalsFilter';
import { GoalsDropdownActionsConnected } from '../../Goals2/GoalSidebar/GoalDropdownActionsConnected';
import { GoalSidebarCreateContent } from '../../Goals2/GoalSidebar/GoalSidebarCreateContent';
import { GoalSidebarUpdateContent } from '../../Goals2/GoalSidebar/GoalSidebarUpdateContent';
import { getHeaderText } from '../../Goals2/GoalSidebar/mappings';
import { GoalsListWithFilter } from '../../Goals2/GoalsList/GoalsListWithFilter';
import { canUpdateGoal } from '../../Goals2/logic';
import { GoalId } from '../../Goals2/types';
import { getDefaultFilter } from './logic';
import { GoalsContextProvider } from '../../Goals2/GoalsContext/GoalsContext';
import { SelectedGoalId } from '../../Goals2/useSelectedGoal';
import { CREATE_GOAL_ID } from '../../goalsLinks';
import Button from '../../../../../../components/button';

type Props = {
  visible: boolean;
  onClose: () => void;
  employeeName: string;
  employeeId: string;
  objectiveTimePeriodId?: string | null;
};

const i18prefix = 'performanceReview.goals2';

// ReviewGoalsSidebar rendered inside the GoalsSectionWithContext so it have GoalsContext
export const ReviewGoalsSidebar: React.FC<Props> = ({
  visible,
  onClose,
  employeeName,
  employeeId,
  objectiveTimePeriodId,
}) => {
  const [selectedGoalId, setSelectedGoalId] = useState<SelectedGoalId>(null);

  const { id: currentEmployeeId } = useUserInfo();
  const { isDirectManager } = useIsManagerMode();
  const isManagerView = isDirectManager(employeeId);

  const [listFilter, setListFilter] = useState<GoalFilter>(
    getDefaultFilter(isManagerView, true, currentEmployeeId, employeeId),
  );

  const { t } = useTranslation();

  const header = useMemo(() => {
    if (selectedGoalId === null) {
      return t(`${i18prefix}.goalSidebar.listHeader`, { name: employeeName });
    } else if (selectedGoalId === CREATE_GOAL_ID) {
      return getHeaderText('create');
    } else {
      return getHeaderText('view');
    }
  }, [selectedGoalId, employeeName, t]);

  const handleCreateGoal = useCallback(() => {
    setSelectedGoalId(CREATE_GOAL_ID);
  }, [setSelectedGoalId]);

  const handleGoalClick = useCallback(
    (goalId: GoalId) => {
      setSelectedGoalId(goalId);
    },
    [setSelectedGoalId],
  );

  const handleBackButtonClick = useCallback(() => {
    setSelectedGoalId(null);
  }, [setSelectedGoalId]);

  const handleClose = useCallback(() => {
    setSelectedGoalId(null);
    onClose();
  }, [setSelectedGoalId, onClose]);

  const getContent = () => {
    if (selectedGoalId === null) {
      return (
        <ListContainer className="p-2">
          <GoalsListWithFilter
            isReviewGoals={true}
            filter={listFilter}
            onFilterChange={setListFilter}
            onCreateGoal={handleCreateGoal}
            onGoalClick={handleGoalClick}
          />
        </ListContainer>
      );
    } else if (selectedGoalId !== CREATE_GOAL_ID) {
      return (
        <GoalSidebarUpdateContent
          goalId={selectedGoalId}
          mode={
            canUpdateGoal(isManagerView, selectedGoalId.type)
              ? 'update'
              : 'view'
          }
          onClose={handleClose}
        />
      );
    } else if (selectedGoalId === CREATE_GOAL_ID) {
      return (
        <GoalSidebarCreateContent
          employeeId={employeeId}
          onClose={handleClose}
        />
      );
    }
    return null;
  };

  const getHeaderActions = () => {
    if (selectedGoalId === null) {
      return (
        <Button
          variant="primary"
          size="sm"
          onClick={handleCreateGoal}
          label={t(`${i18prefix}.createGoal`)}
        />
      );
    } else if (selectedGoalId !== CREATE_GOAL_ID) {
      return (
        <GoalsDropdownActionsConnected
          goalId={selectedGoalId}
          onComplete={onClose}
        />
      );
    }
    return null;
  };

  const content = (
    <GoalsContextProvider
      isManagerView={isManagerView}
      objectiveTimePeriodId={objectiveTimePeriodId}>
      {getContent()}
    </GoalsContextProvider>
  );

  return (
    <ModalSidebar
      show={visible}
      onClose={onClose}
      size={selectedGoalId === null ? 'xl' : undefined}
      header={header}
      content={content}
      headerActions={getHeaderActions()}
      backButton={selectedGoalId !== null}
      onBackButtonClick={handleBackButtonClick}
    />
  );
};

const ListContainer = styled('div')({
  overflowY: 'auto',
  overflowX: 'clip',
});
